import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

/**
 * Wrapper genérico sobre HttpClient.
 * Centraliza la URL base y la construcción de parámetros para el resto de servicios.
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  /** `context` permite marcar la petición con `silentRequest()` (sin toast de error). */
  get<T>(endpoint: string, params?: Record<string, unknown>, context?: HttpContext): Observable<T> {
    return this.http.get<T>(this.url(endpoint), { params: this.toParams(params), context });
  }

  /** GET binario (p. ej. exportación CSV). */
  getBlob(endpoint: string, params?: Record<string, unknown>): Observable<Blob> {
    return this.http.get(this.url(endpoint), {
      params: this.toParams(params),
      responseType: 'blob',
    });
  }

  post<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http.post<T>(this.url(endpoint), body);
  }

  put<T>(endpoint: string, body: unknown): Observable<T> {
    return this.http.put<T>(this.url(endpoint), body);
  }

  patch<T>(endpoint: string, body: unknown, context?: HttpContext): Observable<T> {
    return this.http.patch<T>(this.url(endpoint), body, { context });
  }

  /** `body` es opcional: algunos DELETE del backend leen `req.body` (p. ej. notificaciones). */
  delete<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    options?: { body?: unknown; context?: HttpContext },
  ): Observable<T> {
    return this.http.delete<T>(this.url(endpoint), {
      params: this.toParams(params),
      context: options?.context,
      ...(options?.body !== undefined ? { body: options.body } : {}),
    });
  }

  private url(endpoint: string): string {
    return `${this.baseUrl}/${endpoint.replace(/^\//, '')}`;
  }

  private toParams(params?: Record<string, unknown>): HttpParams {
    let httpParams = new HttpParams();
    if (!params) return httpParams;
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    }
    return httpParams;
  }
}
